import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { usePageMeta } from '../components/Layout';
import { CART_KEY } from './Shop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  usePageMeta('Payment Status — PITFIT Shop', 'Confirming your PITFIT merch order.');
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState(sessionId ? 'checking' : 'error');
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      tries.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === 'paid') {
          localStorage.removeItem(CART_KEY);
          setState('paid');
          return;
        }
        if (['failed', 'expired'].includes(res.data.payment_status)) {
          setState('failed');
          return;
        }
      } catch (e) {
        if (cancelled) return;
        if (e.response && e.response.status === 404) {
          setState('error');
          return;
        }
      }
      if (tries.current >= 10) setState('timeout');
      else setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <section className="section-pad min-h-[70vh] flex items-center">
      <div className="max-w-2xl mx-auto px-5 w-full">
        <div className="glass-card text-center px-8 py-14" style={{ borderColor: 'rgba(125,167,255,0.3)' }} data-testid="payment-success-card">
          {state === 'checking' && (
            <>
              <Loader2 size={40} className="mx-auto animate-spin" style={{ color: 'var(--pf-brand2)' }} />
              <h1 className="font-display font-bold text-3xl text-white mt-6">Confirming your payment…</h1>
              <p className="text-muted-pf mt-3 text-[16px]">Hang tight — this usually takes a couple of seconds.</p>
            </>
          )}
          {state === 'paid' && (
            <>
              <CheckCircle2 size={44} className="mx-auto" style={{ color: 'var(--pf-brand2)' }} />
              <h1 className="font-display font-bold text-3xl text-white mt-6" data-testid="payment-paid-heading">Order confirmed. You earned it.</h1>
              <p className="text-muted-pf mt-3 text-[16px]">Your PITFIT gear is on its way — a Stripe receipt has been emailed to you. See you in the PIT.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <Link to="/shop" className="btn-ghost">Back to Shop</Link>
                <Link to="/book" className="btn-primary">Book a Class <ArrowRight size={16} /></Link>
              </div>
            </>
          )}
          {state === 'timeout' && (
            <>
              <Loader2 size={40} className="mx-auto" style={{ color: 'var(--pf-brand2)' }} />
              <h1 className="font-display font-bold text-3xl text-white mt-6">Payment is still processing</h1>
              <p className="text-muted-pf mt-3 text-[16px]">We haven't received final confirmation yet. Check your email for a Stripe receipt, or reach us at support@pitfitworkout.com.</p>
              <Link to="/shop" className="btn-ghost mt-8 inline-flex">Back to Shop</Link>
            </>
          )}
          {(state === 'failed' || state === 'error') && (
            <>
              <XCircle size={44} className="mx-auto text-red-400" />
              <h1 className="font-display font-bold text-3xl text-white mt-6">Payment didn't go through</h1>
              <p className="text-muted-pf mt-3 text-[16px]">{state === 'error' ? 'Missing payment session — please start checkout again from the shop.' : 'Your card was not charged. Your bag is saved — try again whenever you\u2019re ready.'}</p>
              <Link to="/shop" className="btn-primary mt-8 inline-flex">Return to Shop <ArrowRight size={16} /></Link>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export function PaymentCancel() {
  usePageMeta('Checkout Cancelled — PITFIT Shop', 'Your PITFIT checkout was cancelled.');
  return (
    <section className="section-pad min-h-[70vh] flex items-center">
      <div className="max-w-2xl mx-auto px-5 w-full">
        <div className="glass-card text-center px-8 py-14" data-testid="payment-cancel-card">
          <XCircle size={44} className="mx-auto text-muted-pf" />
          <h1 className="font-display font-bold text-3xl text-white mt-6">Checkout cancelled</h1>
          <p className="text-muted-pf mt-3 text-[16px]">No charge was made. Your bag is saved — pick up where you left off anytime.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/shop" className="btn-primary">Back to the Shop <ArrowRight size={16} /></Link>
            <Link to="/" className="btn-ghost">Home</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
